/**
 * Environment Variable Validation
 * 
 * Validates required environment variables at startup.
 * Throws in development, logs error in production.
 */

interface EnvConfig {
    // Required - app will not function without these
    VITE_FIREBASE_API_KEY: string;
    VITE_FIREBASE_AUTH_DOMAIN: string;
    VITE_FIREBASE_PROJECT_ID: string;

    // Optional but recommended
    VITE_FIREBASE_STORAGE_BUCKET?: string;
    VITE_FIREBASE_MESSAGING_SENDER_ID?: string;
    VITE_FIREBASE_APP_ID?: string;
    VITE_API_URL?: string;
    VITE_SENTRY_DSN?: string;
}

const REQUIRED_VARS = [
    'VITE_FIREBASE_API_KEY',
    'VITE_FIREBASE_AUTH_DOMAIN',
    'VITE_FIREBASE_PROJECT_ID',
] as const;

const OPTIONAL_VARS = [
    'VITE_API_URL',
    'VITE_SENTRY_DSN',
] as const;

/**
 * Validates environment variables and returns typed config.
 * Call this early in app bootstrap to fail fast.
 */
export function validateEnv(): EnvConfig {
    const missing: string[] = [];
    const warnings: string[] = [];

    // Check required vars
    for (const key of REQUIRED_VARS) {
        const value = import.meta.env[key];
        if (!value) {
            missing.push(key);
        }
    }

    // Check optional but recommended vars
    for (const key of OPTIONAL_VARS) {
        const value = import.meta.env[key];
        if (!value) {
            warnings.push(key);
        }
    }

    // Report missing required vars
    if (missing.length > 0) {
        const message = `Missing required environment variables:\n${missing.map(k => `  - ${k}`).join('\n')}\n\nCheck your .env file or Vercel environment settings.`;

        if (import.meta.env.DEV) {
            throw new Error(message);
        } else {
            console.error('[ENV] ' + message);
        }
    }

    // Warn about optional vars (only in dev)
    if (warnings.length > 0 && import.meta.env.DEV) {
        console.warn(
            `[ENV] Optional environment variables not set:\n${warnings.map(k => `  - ${k}`).join('\n')}`
        );
    }

    return {
        VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
        VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
        VITE_API_URL: import.meta.env.VITE_API_URL,
        VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN,
    };
}

/**
 * Get API base URL with fallback.
 * Throws helpful error if not configured in production.
 */
export function getApiUrl(): string {
    const url = import.meta.env.VITE_API_URL;

    if (!url) {
        if (import.meta.env.DEV) {
            // Default to local dev backend
            return 'http://localhost:3001/api/v1';
        }
        // Production without API URL is a configuration error
        console.error('[ENV] VITE_API_URL not set - API calls will fail');
        return '';
    }

    return url;
}
